import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { FilterResetButton } from './FilterResetButton';
import { FilterExcludedCountBadge } from './FilterExcludedCountBadge';

interface ToggleBadgeSectionProps {
  label: string;
  items: ReadonlyArray<{ code: string; label: string }>;
  excluded: Set<string>;
  /** Codes that have 0 securities — shown as disabled and non-interactive */
  disabledCodes?: Set<string>;
  onToggle: (code: string) => void;
  onExcludeAll: (codesToExclude: Array<string>) => void;
  /** Called when "Include All" is clicked. If not provided, falls back to onExcludeAll (toggle behavior). */
  onIncludeAll?: () => void;
  /** Minimum number of items that must remain included. Defaults to 0. */
  minIncluded?: number;
  /** Clears all exclusions — renders a Figma-style Reset by the label when set. */
  onReset?: () => void;
  /** Securities removed by these exclusions — renders the shared "N excluded" badge. */
  excludedSecurityCount?: number;
}

export function ToggleBadgeSection({
  label,
  items,
  excluded,
  disabledCodes,
  onToggle,
  onExcludeAll,
  onIncludeAll,
  minIncluded = 0,
  onReset,
  excludedSecurityCount = 0,
}: ToggleBadgeSectionProps) {
  const activeItems = disabledCodes
    ? items.filter((item) => !disabledCodes.has(item.code))
    : items;
  const includedCount = activeItems.filter(
    (item) => !excluded.has(item.code),
  ).length;
  const allExcluded = includedCount === 0;

  const handleToggle = (code: string) => {
    if (disabledCodes?.has(code)) return;
    const isCurrentlyIncluded = !excluded.has(code);
    if (isCurrentlyIncluded && includedCount <= minIncluded) return;
    onToggle(code);
  };

  const atMinimum = minIncluded > 0 && includedCount <= minIncluded;

  const handleExcludeAll = () => {
    if (allExcluded || atMinimum) {
      if (onIncludeAll) onIncludeAll();
      else onExcludeAll([]); // Pass empty array to clear exclusions
    } else {
      const includedCodes = items
        .filter((i) => !excluded.has(i.code))
        .map((i) => i.code);
      const codesToKeep = new Set(includedCodes.slice(0, minIncluded));

      const finalCodesToExclude = items
        .filter((i) => !codesToKeep.has(i.code))
        .map((i) => i.code);

      onExcludeAll(finalCodesToExclude);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[15px] font-medium text-muted-foreground">
            {label}
          </span>
          {onReset && excluded.size > 0 && (
            <FilterResetButton onClick={onReset} />
          )}
        </div>
        <div className="flex items-center gap-2">
          {excludedSecurityCount > 0 && (
            <FilterExcludedCountBadge count={excludedSecurityCount} />
          )}
          <button
            type="button"
            onClick={handleExcludeAll}
            className="text-xs text-muted-foreground hover:text-red-500 transition-colors"
          >
            {allExcluded || atMinimum ? 'Include All' : 'Exclude All'}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {activeItems.map((item) => {
          const isDisabled = disabledCodes?.has(item.code) ?? false;
          const isExcluded = excluded.has(item.code);
          const isLastIncluded =
            !isDisabled && !isExcluded && includedCount <= minIncluded;
          return (
            <Badge
              key={item.code}
              variant="outline"
              className={cn(
                'transition-colors select-none h-7 px-3 text-xs',
                isDisabled
                  ? 'border-border bg-muted/50 text-muted-foreground cursor-not-allowed'
                  : isLastIncluded
                    ? 'border-primary/30 bg-primary/10 text-primary/40 cursor-not-allowed'
                    : isExcluded
                      ? 'opacity-60 hover:bg-muted/50 cursor-pointer'
                      : 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 cursor-pointer',
              )}
              onClick={() => handleToggle(item.code)}
            >
              {item.label}
            </Badge>
          );
        })}
      </div>
    </div>
  );
}
